import { Injectable, computed, effect, inject, signal } from '@angular/core';
import { I18nService } from '../../../core';
import { BillingGateService } from '../../../core/billing-gate';
import { TenantService } from '../../../core/tenant/tenant.service';
import { OperationsApiService } from '../../../core/api';
import type { CardApiContract } from '../../../core/api';
import type { CardUiModel } from '../../../core/types';
import { mapCardContractToUiModel } from '../adapters/card.adapter';

@Injectable({ providedIn: 'root' })
export class CardsStore {
  private readonly i18n = inject(I18nService);
  private readonly tenant = inject(TenantService);
  private readonly api = inject(OperationsApiService);
  private readonly billingGate = inject(BillingGateService);
  private readonly loadedCompanyIdSignal = signal<string | null>(null);

  private readonly cardsSignal = signal<CardUiModel[]>([]);
  private readonly loadingSignal = signal<boolean>(false);
  private readonly errorSignal = signal<string | null>(null);

  readonly cards = this.cardsSignal.asReadonly();
  readonly loading = this.loadingSignal.asReadonly();
  readonly error = this.errorSignal.asReadonly();
  readonly hasData = computed(() => this.cards().length > 0);

  /** Estado anterior do gate — usado pra detectar transição true→false e retentar o load. */
  private wasBillingGateBlocked = false;

  constructor() {
    effect(() => {
      const companyId = this.tenant.companyId();
      if (!companyId) {
        this.reset();
        return;
      }
      if (this.loadedCompanyIdSignal() === companyId) return;
      this.loadedCompanyIdSignal.set(companyId);
      this.load();
    });

    // Quando o billing gate é liberado (ex.: usuário pagou e voltou Canceled→Active),
    // retenta o load se houve erro. Sem isso, o `cards.store.loadError` ficava grudado
    // até refresh manual, porque o effect acima não re-roda pra mesma empresa.
    effect(() => {
      const isBlocked = this.billingGate.blocked();
      const justUnblocked = this.wasBillingGateBlocked && !isBlocked;
      this.wasBillingGateBlocked = isBlocked;
      if (justUnblocked && this.errorSignal() && this.tenant.companyId()) {
        this.load();
      }
    });
  }

  load(): void {
    const requestCompanyId = this.tenant.companyId();
    if (!requestCompanyId) return;

    this.loadingSignal.set(true);
    this.errorSignal.set(null);
    void this.api
      .getWorkflows()
      .then(async (workflows) => {
        if (this.tenant.companyId() !== requestCompanyId) return;
        /**
         * `allSettled` evita que uma falha pontual num workflow (403/404/500 num único endpoint)
         * derrube todos os cards e mostre o banner de erro no dashboard. Mantemos só os fulfilled
         * e exibimos erro apenas se TUDO falhou (cenário real de "não consegui carregar nada").
         */
        const settled = await Promise.allSettled(
          workflows.map((workflow) => this.api.getCardsByWorkflow(workflow.id))
        );
        if (this.tenant.companyId() !== requestCompanyId) return;
        const cards = settled
          .filter(
            (result): result is PromiseFulfilledResult<CardApiContract[]> =>
              result.status === 'fulfilled'
          )
          .flatMap((result) => result.value);
        this.cardsSignal.set(cards.map(mapCardContractToUiModel));
        const allFailed =
          workflows.length > 0 && settled.every((result) => result.status === 'rejected');
        if (allFailed) {
          this.errorSignal.set(this.i18n.t('cards.store.loadError'));
        }
      })
      .catch(() => {
        // Só cai aqui se `getWorkflows()` falhar — listagem global indisponível.
        if (this.tenant.companyId() !== requestCompanyId) return;
        this.errorSignal.set(this.i18n.t('cards.store.loadError'));
        this.cardsSignal.set([]);
      })
      .finally(() => {
        if (this.tenant.companyId() !== requestCompanyId) return;
        this.loadingSignal.set(false);
      });
  }

  getById(cardId: string | null): CardUiModel | null {
    if (!cardId) return null;
    return this.cards().find((card) => card.id === cardId) ?? null;
  }

  getByProject(projectId: string | null): CardUiModel[] {
    if (!projectId) return [];
    return this.cards().filter((card) => card.projectId === projectId);
  }

  getByWorkflow(workflowId: string | null): CardUiModel[] {
    if (!workflowId) return [];
    return this.cards().filter((card) => card.workflowId === workflowId);
  }

  reset(): void {
    this.loadedCompanyIdSignal.set(null);
    this.cardsSignal.set([]);
    this.loadingSignal.set(false);
    this.errorSignal.set(null);
  }
}
